class CartDrawer extends HTMLElement {
  // Class-scoped, not module-scoped: every asset here is a classic script sharing
  // one global lexical scope, so a top-level const collides across components.
  static FOCUSABLE_SELECTORS =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  static TRANSITION_DURATION = 300;
  static SCROLL_LOCK_CLASS = 'overflow-hidden';
  static MORPH_TARGETS = ['[data-cart-header]', '[data-cart-body]', '[data-cart-footer]'];

  constructor() {
    super();
    this.previousActiveElement = null;
    this.isOpen = false;
    this.focusTrapHandler = null;
    this._checkoutPending = false;
    this._checkoutUrl = '';
    this._checkoutMode = 'native';
    this._qtyState = CartQuantity.createState();
    this._closeTimer = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('[data-cart-panel]');
    const initialCheckoutBtn = this.querySelector('[data-cart-checkout]');
    this._checkoutUrl = initialCheckoutBtn?.dataset.checkoutUrl ?? '';
    this._checkoutMode = initialCheckoutBtn?.dataset.checkoutMode ?? 'native';

    this.overlay?.addEventListener('click', () => this.close());

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
    });

    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.handleKeydown);

    this.handleCartTrigger = (event) => {
      if (event.target.closest('[data-cart-trigger]')) {
        event.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this.handleCartTrigger);

    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cart-checkout]');
      if (!btn) return;

      // The footer is a morph target, so re-read state off the live button.
      this._checkoutUrl = btn.dataset.checkoutUrl ?? this._checkoutUrl;
      this._checkoutMode = btn.dataset.checkoutMode ?? this._checkoutMode;

      // GoKwik owns its own click handler once its SDK has enabled the button.
      // Until then the button is disabled, so a click that reaches us means the
      // SDK isn't ready — fall through to the native redirect as a safety net.
      if (this._checkoutMode === 'gokwik' && typeof window.onCheckoutClick === 'function') {
        this._flushPendingQuantities();
        if (CartMutations.pending > 0) {
          this._checkoutPending = true;
          this._setCheckoutBusy(true);
        } else {
          window.onCheckoutClick(btn);
        }
        return;
      }

      // A coalescing window still holding clicks has not reached CartMutations, so
      // `pending` would read 0 and the redirect would drop the burst. Flushing makes
      // it pending — synchronously, because run() counts before it awaits — which is
      // exactly what the branch below then waits on.
      this._flushPendingQuantities();

      if (CartMutations.pending > 0) {
        this._checkoutPending = true;
        this._setCheckoutBusy(true);
      } else {
        window.location.href = this._checkoutUrl;
      }
    });

    this.addEventListener('click', (event) => this._handleStepper(event));

    this.addEventListener('click', (event) => this._handleAdd(event));

    this.addEventListener('click', (event) => this._handleRemoveDiscount(event));

    // No `cart:mutation-start` listener by design: nothing but a click on checkout
    // itself may mark checkout busy. A mutation the shopper started elsewhere — an
    // add, a gift tile, a promo code — must leave the button live.
    this.handleMutationEnd = (event) => {
      if ((event.detail?.pending ?? 0) > 0) return;
      this._setCheckoutBusy(false);
      CartOptimistic.clearAmountsStale(this);

      if (this._checkoutPending) {
        this._checkoutPending = false;
        if (this._checkoutMode === 'gokwik' && typeof window.onCheckoutClick === 'function') {
          const liveBtn = this.querySelector('[data-cart-checkout]');
          window.onCheckoutClick(liveBtn);
        } else {
          window.location.href = this._checkoutUrl;
        }
      }
    };
    document.addEventListener('cart:mutation-end', this.handleMutationEnd);

    CartStore.init();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleCartTrigger);
    document.removeEventListener('cart:mutation-end', this.handleMutationEnd);
    CartQuantity.clearPending(this._qtyState);
    // In the theme editor this is a section re-render: the incoming markup carries no
    // predictions, so any held rollback entry is a stale key.
    CartOptimistic.reset();
    clearTimeout(this._closeTimer);
    this._closeTimer = null;
    this.removeTrapFocus();
  }

  open() {
    // Cancels a pending close. Visibility runs on two clocks — the synchronous
    // state flags and the deferred `hidden` flip at the end of the transition —
    // and only an owned handle keeps a reopen inside that window from being
    // undone by the previous close's timer.
    clearTimeout(this._closeTimer);
    this._closeTimer = null;

    this.previousActiveElement = document.activeElement;
    this.hidden = false;
    this.isOpen = true;

    // Two frames: the panel must paint in its closed state before the
    // state flip, or the browser has nothing to transition from.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setAttribute('data-cart-state', 'open');
        this.setAttribute('aria-hidden', 'false');
        this.lockScroll();
        this.trapFocus();

        const first = this._focusables()[0];
        if (first) first.focus();
        else this.panel?.focus();
      });
    });
  }

  close() {
    // Focus leaves before aria-hidden lands, so it is never inside a hidden subtree.
    this._restoreOuterFocus();

    this.isOpen = false;
    this.removeTrapFocus();
    this.setAttribute('data-cart-state', 'closed');
    this.setAttribute('aria-hidden', 'true');
    this.unlockScroll();

    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      this._closeTimer = null;
      this.hidden = true;
    }, CartDrawer.TRANSITION_DURATION);
  }

  _restoreOuterFocus() {
    const previous = this.previousActiveElement;
    if (previous?.isConnected) {
      previous.focus({ preventScroll: true });
      return;
    }
    document.querySelector('[data-cart-trigger]')?.focus({ preventScroll: true });
  }

  lockScroll() {
    document.body.classList.add(CartDrawer.SCROLL_LOCK_CLASS);
  }

  unlockScroll() {
    document.body.classList.remove(CartDrawer.SCROLL_LOCK_CLASS);
  }

  // Queried per keypress, never cached — a morph would otherwise leave the trap
  // holding detached nodes. offsetParent filters out hidden listbox contents.
  _focusables() {
    if (!this.panel) return [];
    return [...this.panel.querySelectorAll(CartDrawer.FOCUSABLE_SELECTORS)].filter((el) => el.offsetParent !== null);
  }

  trapFocus() {
    if (!this.panel) return;
    this.removeTrapFocus();

    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;

      const focusable = this._focusables();

      // The shell can legitimately hold no focusable element while regions are
      // still unbuilt. Focus the panel itself so focus never escapes the scrim.
      if (!focusable.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };
    this.panel.addEventListener('keydown', this.focusTrapHandler);
  }

  removeTrapFocus() {
    if (this.focusTrapHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  _flushPendingQuantities() {
    CartQuantity.flush(this._qtyState, (updates, control) => this.flushQuantities(updates, control));
  }

  _handleStepper(event) {
    CartQuantity.handleStepperClick(event, {
      state: this._qtyState,
      onFlush: (updates, control) => this.flushQuantities(updates, control),
      onMax: (key, control, max) => {
        const boundMessage = this.announcement('announceQtyMax', { '%max%': max });
        if (!boundMessage) return;

        CartMutations.showError(this, { control, lineKey: key, message: boundMessage });
        CartMutations.announce(this, boundMessage);
      },
    });
  }

  // Delegated on the drawer root, which sits outside every MORPH_TARGET, so any
  // region can emit [data-cart-add] without binding a listener per rendered card.
  _handleAdd(event) {
    const btn = event.target.closest('[data-cart-add]');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    event.preventDefault();

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', btn.dataset.quantity ?? '1');

    // The button is both the error anchor and the source of the product data the
    // optimistic row is painted from. It is never a loader: see addFromForm.
    this.addFromForm(formData, { control: btn, source: btn });
  }

  _handleRemoveDiscount(event) {
    const btn = event.target.closest('[data-cart-remove-discount]');
    if (!btn) return;

    event.preventDefault();

    return this.removeDiscount(btn);
  }

  // Templates live in Liquid as data-announce-* on the root, so no locale
  // strings leak into JS. Missing attribute → no announcement.
  announcement(datasetKey, replacements = {}) {
    const template = this.dataset[datasetKey];
    if (!template) return null;

    let message = template;
    for (const [token, value] of Object.entries(replacements)) {
      message = message.replace(token, value ?? '');
    }
    return message;
  }

  // The source fallback covers the PDP add, which passes no control and would
  // otherwise announce an empty product name.
  _productTitle(control, source = null) {
    return control?.dataset.productTitle ?? source?.dataset.productTitle ?? '';
  }

  mutate(requestFn, { control = null, lineKey = null, announce = null, busy = true } = {}) {
    return CartMutations.run({
      requestFn,
      root: this,
      sectionId: this.dataset.sectionId ?? null,
      morphTargets: CartDrawer.MORPH_TARGETS,
      scrollTarget: '[data-cart-body]',
      control,
      lineKey,
      announce,
      busy,
    });
  }

  // One request for a whole burst, whatever it touched — `updates` is a Map of line
  // key to final quantity, a removal being a zero. The lines were already painted on
  // click; CartMutations.settleLine owns what happens to those predictions,
  // identically here and on the cart page.
  //
  // `/cart/update.js` rather than change.js: it addresses several lines at once and
  // takes quantities and zeros in the same payload, which is what lets a removal ride
  // along with another line's pending quantity instead of cancelling it.
  async flushQuantities(updates, control = null) {
    // A merged row sends several line keys but is still the one line the customer touched.
    const rows = updates.rows ?? updates;
    const isSingleLine = rows.size === 1;
    const keys = [...updates.keys()];

    // Freezes the clicks predicted so far as this request's slice of each count.
    for (const key of keys) CartOptimistic.dispatch(key);

    const result = await this.mutate((sections) => CartAPI.update({ updates: Object.fromEntries(updates) }, sections), {
      // A snapshot spanning several lines has no single owning control or line, so
      // a failure resolves to the panel's global slot instead of one arbitrary row.
      control: isSingleLine ? control : null,
      lineKey: isSingleLine ? [...rows.keys()][0] : null,
      announce: this._quantityAnnouncement(rows),
      busy: false,
    });

    for (const key of keys) {
      CartMutations.settleLine(result, { key, root: this, pending: this._qtyState.pending });
    }

    return result;
  }

  // The row is read here, before the request: it is still in the DOM even for a
  // removal, which collapses the line in place and leaves the morph to remove it.
  // A batch spanning several rows is described by no per-product template, so it
  // falls back to the generic message. `rows` maps each row's key to its total.
  _quantityAnnouncement(rows) {
    if (rows.size !== 1) return this.announcement('announceCartUpdated');

    const [[key, quantity]] = rows;
    const product = this.querySelector(`[data-item-key="${CSS.escape(key)}"]`)?.dataset.productTitle ?? '';

    if (quantity === 0) return this.announcement('announceRemoved', { '%product%': product });
    return this.announcement('announceQuantity', { '%product%': product, '%quantity%': quantity });
  }

  // `source` carries the add's product data; it is separate from `control` because a
  // caller owning its own loader still has data worth painting a row from.
  async addFromForm(formData, { control = null, source = null } = {}) {
    const variantId = formData.get('id');
    const quantity = Number.parseInt(formData.get('quantity') ?? '1', 10) || 1;
    const message = this.announcement('announceAdded', { '%product%': this._productTitle(control, source) });

    CartOptimistic.markUnresolved(this, variantId, { quantity, source });
    // Freezes this add's own slice of the count, so an overlapping add of the same
    // variant cannot have its rollback spent by whichever request resolves first.
    CartOptimistic.dispatchAdd(variantId);

    // `busy: false` because this path always predicts: markUnresolved has already put
    // a row in the list or shimmered the matching one, so a spinner over the button
    // would blank the only control the shopper is looking at to cover a round trip
    // whose result is already on screen. `control` still resolves the error slot.
    // addItems is deliberately not given the same treatment — it predicts nothing,
    // so there its spinner is the only feedback there is.
    const result = await this.mutate((sections) => CartAPI.add(formData, sections), {
      control,
      announce: message,
      busy: false,
    });

    // isLatest is load-bearing: a superseded add must not remove a row a newer
    // mutation has since rendered.
    if (result?.ok) CartOptimistic.settleAdd(variantId);
    else if (CartMutations.isLatest(result?.seq)) CartOptimistic.clearUnresolved(this, variantId);

    return result;
  }

  addItems(items, control = null) {
    const message = this.announcement('announceAdded', { '%product%': this._productTitle(control) });
    return this.mutate((sections) => CartAPI.addMultiple(items, sections), { control, announce: message });
  }

  // `/cart/update.js` clears every applied code — the platform offers no selective removal.
  removeDiscount(control = null) {
    const message = this.announcement('announceCodeRemoved');
    return this.mutate((sections) => CartAPI.update({ discount: '' }, sections), { control, announce: message });
  }

  _setCheckoutBusy(isBusy) {
    const checkoutBtn = this.querySelector('[data-cart-checkout]');
    const spinnerEl = this.querySelector('[data-checkout-spinner]');

    if (checkoutBtn) {
      checkoutBtn.disabled = isBusy;
      if (isBusy) {
        checkoutBtn.setAttribute('aria-disabled', 'true');
        checkoutBtn.setAttribute('aria-busy', 'true');
      } else {
        checkoutBtn.removeAttribute('aria-disabled');
        checkoutBtn.removeAttribute('aria-busy');
      }
    }

    if (spinnerEl) spinnerEl.hidden = !isBusy;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
